import { useEffect, useMemo } from "react";

import {
  Button,
  formatNumber,
  LoadingSkeleton,
  useBreakpoints
} from "@narmi/design_system";
import { entities, modules } from "byzantine";
import { REQUEST_STATUS } from "byzantine/src/dbbl/composites/request/request.types";
import { useLocalization } from "@fluent/react";

import BorderCard from "../../BorderCard";
import { useSubscriptionCards } from "../../contexts/SubscriptionCardsContext";
import { useDashboardHiddenSections } from "../../contexts/DashboardHiddenSectionsContext";
import MerchantIcon from "../MerchantIcon";
import SubscriptionsChart from "../SubscriptionsChart";
import styles from "./SubscriptionsPreviewCard.module.scss";

const { SECTION } = modules.userDisplayPreferences;

const SubscriptionsPreviewCard = () => {
  const { l10n } = useLocalization();
  const { cards, isLoading } = useSubscriptionCards();
  const { setSectionHidden } = useDashboardHiddenSections();
  // For now we'll default to the first card, we may modify this in
  // the future.
  const cardId = cards[0]?.id;
  const { s, m, l, xl } = useBreakpoints();
  const screenSize = useMemo<
    keyof typeof modules.subscriptions.webConfigs
  >(() => {
    switch (true) {
      case xl:
        return "xl";
      case l:
        return "l";
      case m:
        return "m";
      case s:
        return "s";
      default:
        return "xs";
    }
  }, [s, m, l, xl]);
  const {
    data,
    monthLabels,
    selectedMonth,
    prevMonth,
    nextMonth,
    selectedMonthFullName,
    year,
    isSelectedThisMonth,
    prev12MonthsTotal,
    monthTotal,
    selectedTransactions,
    onMonthChange
  } = modules.subscriptions.useSubscriptionsChartData(cardId, screenSize, 6);

  const subscriptionTransactionsPreview = useMemo(
    () =>
      [...(selectedTransactions ?? [])]
        .sort((a, b) => Number(b.amount) - Number(a.amount))
        .slice(0, 6),
    [selectedTransactions]
  );
  const undisplayedTransactionsCount = selectedTransactions
    ? selectedTransactions.length - 6
    : 0;
  const title = l10n.getString("subscription-card-title");
  const viewAllLabel = l10n.getString("subscriptions-card-view-all");
  const moreLabel = l10n.getString("subscriptions-card-preview-more-count", {
    count: undisplayedTransactionsCount
  });

  const { send: sendSubscriptions, status: subscriptionsStatus } =
    entities.subscriptions.useSubscriptionRequests();

  useEffect(() => {
    if (cardId) {
      sendSubscriptions(cardId);
    }
  }, [cardId, sendSubscriptions]);

  const { send: sendMerchants, loading: isLoadingMerchants } =
    entities.merchants.useFetchMerchantsRequest();

  useEffect(() => {
    sendMerchants();
  }, [sendMerchants]);

  const isLoadingSubscriptions =
    subscriptionsStatus === REQUEST_STATUS.idle ||
    subscriptionsStatus === REQUEST_STATUS.loading;

  const isPreviewLoading =
    isLoading || isLoadingSubscriptions || isLoadingMerchants;

  const isTotalTransactionsZero = useMemo(
    () => data.every(({ total }) => total === 0),
    [data]
  );

  // Collapse the dashboard slot once we know there is no spend to preview,
  // rather than rendering an empty card.
  useEffect(() => {
    if (isPreviewLoading || subscriptionsStatus !== REQUEST_STATUS.success)
      return;

    setSectionHidden(SECTION.SUBSCRIPTIONS, isTotalTransactionsZero);
  }, [
    subscriptionsStatus,
    isLoading,
    isLoadingMerchants,
    isTotalTransactionsZero,
    setSectionHidden
  ]);

  // Prevent rendering an empty card on mobile
  if (!isPreviewLoading && isTotalTransactionsZero) {
    return null;
  }

  if (isPreviewLoading) {
    return (
      <BorderCard title={title}>
        <LoadingSkeleton isLoading />
      </BorderCard>
    );
  }

  if (!cardId) {
    return (
      <BorderCard title={title}>
        {l10n.getString("subscriptions-card-no-cards", null)}
      </BorderCard>
    );
  }

  return (
    <BorderCard title={title}>
      <div className={styles.layout}>
        <SubscriptionsChart
          data={data}
          monthLabels={monthLabels}
          selectedMonth={selectedMonth}
          prevMonth={prevMonth}
          nextMonth={nextMonth}
          selectedMonthFullName={selectedMonthFullName}
          year={year}
          isSelectedThisMonth={isSelectedThisMonth}
          prev12MonthsTotal={prev12MonthsTotal}
          monthTotal={monthTotal}
          onMonthChange={onMonthChange}
          screenSize="m"
          minimal
        />
        <div className={styles.subscriptions}>
          {subscriptionTransactionsPreview?.map((transaction) => (
            <div key={transaction.id} className={styles["subscriptions-box"]}>
              <div className={styles["subscriptions-box-merchant"]}>
                <MerchantIcon name={transaction.merchant_name || ""} size="s" />
                <span>{transaction.merchant_name}</span>
              </div>
              <span className={styles["subscriptions-box-amount"]}>
                {formatNumber(transaction.amount, "currency")}
              </span>
            </div>
          ))}
        </div>
        <div className={styles.footer}>
          {undisplayedTransactionsCount > 0 && <span>{moreLabel}</span>}
          <Button
            onClick={() => window.location.assign("/cards/subscriptions")}
          >
            {viewAllLabel}
          </Button>
        </div>
      </div>
    </BorderCard>
  );
};

export default SubscriptionsPreviewCard;
